import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Ticket, CheckCircle, Clock, Calendar, DollarSign, Receipt } from 'lucide-react';
import { getErrorMessage } from '@/utils/errorHandler';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function JoinedEvents() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [receiptForm, setReceiptForm] = useState({
    receipt_number: '',
    transaction_amount: '',
    transaction_date: '',
    person_helped: '',
    receipt_image_url: ''
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  // System settings
  const [showReceiptImageUpload, setShowReceiptImageUpload] = useState(false);

  useEffect(() => {
    fetchJoinedEvents();
  }, []);

  const fetchJoinedEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Use dedicated endpoint for joined events
      const response = await axios.get(`${API}/customers/my-events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching joined events:', error);
      setEvents([]);
    }
  };

  const handleSubmitReceipt = async (event) => {
    setSelectedEvent(event);
    setReceiptForm({
      receipt_number: '',
      transaction_amount: '',
      transaction_date: '',
      person_helped: '',
      receipt_image_url: ''
    });
    setImagePreview(null);
    
    // Fetch business settings to check if receipt upload is enabled
    try {
      const response = await axios.get(`${API}/public/business/${event.business_id}`);
      setShowReceiptImageUpload(response.data.show_receipt_image_upload === true);
    } catch (error) {
      console.error('Error fetching business settings:', error);
      // Default to false if fetch fails (safe default)
      setShowReceiptImageUpload(false);
    }
    
    setShowSubmitDialog(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, PNG, and PDF files are allowed');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/receipts/upload/${selectedEvent.event_id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setReceiptForm({ ...receiptForm, receipt_image_url: response.data.image_url });
      setImagePreview(URL.createObjectURL(file));
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleReceiptSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/receipts/submit`, {
        event_id: selectedEvent.event_id,
        business_name: selectedEvent.business_name,
        ...receiptForm
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Receipt submitted successfully!');
      setShowSubmitDialog(false);
      setReceiptForm({
        receipt_number: '',
        transaction_amount: '',
        transaction_date: '',
        person_helped: '',
        receipt_image_url: ''
      });
      setImagePreview(null);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to submit receipt'));
    }
  };

  return (
    <div className="min-h-screen bg-cream pt-6 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Button
          onClick={() => navigate('/customer/events')}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Events
        </Button>

        <div className="mb-8">
          <h1 className="font-playfair font-bold text-4xl text-primary mb-2">Joined Events</h1>
          <p className="font-outfit text-gray-600">Events you can participate in or requested to join</p>
        </div>

        {(events || []).length === 0 ? (
          <Card className="p-12 text-center">
            <Ticket className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p className="font-outfit text-gray-600">You haven't joined any events yet</p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {(events || []).map((event) => (
              <Card key={event.event_id} className="p-6 hover:shadow-lg transition-shadow">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-playfair font-bold text-2xl text-primary">{event.event_name}</h3>
                  <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">{event.event_id}</span>
                </div>
                <p className="font-outfit text-sm text-gray-500 mb-2">By {event.business_name}</p>
                <p className="font-outfit text-gray-600 text-sm mb-4">{event.event_description}</p>
                
                <div className="space-y-2 mb-4">
                  {/* Only show coupon value if not hidden by business */}
                  {!event.coupon_value_hidden && (
                    <div className="flex items-center text-sm">
                      <DollarSign className="w-4 h-4 text-amber-600 mr-2" />
                      <span className="font-outfit">Incentive Points: {event.coupon_value}</span>
                    </div>
                  )}
                  <div className="flex items-center text-sm">
                    <Calendar className="w-4 h-4 text-amber-600 mr-2" />
                    <span className="font-outfit">Ends: {new Date(event.end_date).toLocaleDateString()}</span>
                  </div>
                </div>

                <div className="border-t pt-4 mb-4">
                  <p className="font-outfit font-semibold text-sm text-gray-700 mb-2">Prizes:</p>
                  <ul className="space-y-1">
                    {(event.prizes || []).map((prize, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-amber-600 mt-0.5">•</span>
                        <div className="flex-1">
                          <span className="font-semibold text-gray-800">{prize.prize_name || `Prize ${idx + 1}`}</span>
                          {prize.prize_description && (
                            <span className="text-gray-600"> - {prize.prize_description}</span>
                          )}
                          <span className="text-amber-600 font-semibold ml-2">${prize.prize_amount}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                  {event.requested_at && (
                    <p className="font-outfit text-xs text-gray-500 mt-2">
                      Requested: {new Date(event.requested_at).toLocaleDateString()}
                    </p>
                  )}
                </div>

                {event.membership_status === 'approved' ? (
                  <div className="space-y-2">
                    <div className="flex items-center text-sm text-green-600 font-outfit">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Joined
                    </div>
                    {event.status === 'completed' ? (
                      <Button
                        className="w-full bg-gray-400 cursor-not-allowed"
                        size="sm"
                        disabled
                      >
                        Draw Complete
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleSubmitReceipt(event)}
                        className="w-full bg-amber-600 hover:bg-amber-700"
                        size="sm"
                      >
                        <Receipt className="w-4 h-4 mr-2" />
                        Submit Receipt
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center text-sm text-yellow-600 font-outfit">
                    <Clock className="w-4 h-4 mr-2" />
                    Pending Approval
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}

        {/* Submit Receipt Dialog */}
        <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
          <DialogContent className="max-w-md rounded-3xl">
            <DialogHeader>
              <DialogTitle className="font-playfair text-2xl">Submit Receipt</DialogTitle>
            </DialogHeader>
            {selectedEvent && (
              <div className="space-y-4 font-outfit">
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-600">Event: <strong>{selectedEvent.event_name}</strong></p>
                  <p className="text-sm text-gray-600">Business: {selectedEvent.business_name}</p>
                  {/* Only show coupon value if not hidden */}
                  {!selectedEvent.coupon_value_hidden && (
                    <p className="text-sm text-gray-600">Incentive Points: {selectedEvent.coupon_value}</p>
                  )}
                </div>
                <form onSubmit={handleReceiptSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="receipt_number">Receipt Number *</Label>
                    <Input
                      id="receipt_number"
                      type="text"
                      value={receiptForm.receipt_number}
                      onChange={(e) => setReceiptForm({ ...receiptForm, receipt_number: e.target.value })}
                      required
                      placeholder="Enter receipt number"
                    />
                  </div>
                  <div>
                    <Label htmlFor="transaction_amount">Transaction Amount ($) *</Label>
                    <Input
                      id="transaction_amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={receiptForm.transaction_amount}
                      onChange={(e) => setReceiptForm({ ...receiptForm, transaction_amount: e.target.value })}
                      required
                      placeholder="0.00"
                    />
                    {/* Only show coupon calculation if not hidden */}
                    {receiptForm.transaction_amount && selectedEvent && !selectedEvent.coupon_value_hidden && (
                      <p className="text-xs text-gray-500 mt-1">
                        Will generate: {Math.ceil(parseFloat(receiptForm.transaction_amount) / selectedEvent.coupon_value)} points
                      </p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="transaction_date">Transaction Date *</Label>
                    <Input
                      id="transaction_date"
                      type="date"
                      value={receiptForm.transaction_date}
                      onChange={(e) => setReceiptForm({ ...receiptForm, transaction_date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="person_helped">Person Helped (Optional)</Label>
                    <Textarea
                      id="person_helped"
                      value={receiptForm.person_helped}
                      onChange={(e) => setReceiptForm({ ...receiptForm, person_helped: e.target.value })}
                      placeholder="Who did you help with this purchase?"
                      rows={3}
                    />
                  </div>
                  {showReceiptImageUpload && (
                    <div>
                      <Label>Receipt Image (Optional)</Label>
                      <Input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,application/pdf"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                      />
                      <p className="text-xs text-gray-500 mt-1">JPG, PNG, or PDF - Max 5MB</p>
                      {uploadingImage && (
                        <p className="text-xs text-blue-600 mt-2">Uploading image...</p>
                      )}
                      {imagePreview && !uploadingImage && (
                        <div className="mt-2">
                          <p className="text-xs text-green-600">✓ Image uploaded successfully</p>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2 pt-2">
                    <Button 
                      type="submit" 
                      className="flex-1 bg-primary hover:bg-primary/90"
                      disabled={uploadingImage}
                    >
                      Submit Receipt
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowSubmitDialog(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
